import { motion } from 'framer-motion'
import {
  FileCheck,
  Headphones,
  LayoutDashboard,
  Code2,
  Shield,
  Check,
  FileText,
  Calendar,
  ClipboardList,
  Users,
  Bell,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageMeta } from '../components/seo/PageMeta'
import { Button } from '../components/ui/Button'
import { SectionLabel } from '../components/ui/SectionLabel'
import { pageMeta } from '../lib/seo'

const aiAutomationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'AI Business Automation',
  provider: { '@type': 'Organization', name: 'NextTech Consultants' },
  description: 'Workflow automation and AI implementation for Pacific Northwest SMBs.',
  areaServed: 'Pacific Northwest',
  serviceType: 'AI Business Automation',
}

const exampleAutomations = [
  { icon: ClipboardList, label: 'Client intake → CRM + calendar booking → welcome email sequence' },
  { icon: FileCheck, label: 'Compliance document generation from your existing tech stack' },
  { icon: FileText, label: 'Invoice generation from completed project data' },
  { icon: LayoutDashboard, label: 'Weekly report generation from your project management tools' },
  { icon: Users, label: 'New employee IT provisioning and account setup' },
  { icon: Bell, label: 'Appointment reminders and follow-up sequences' },
  { icon: Shield, label: 'Security assessment responses from your environment data' },
]

const processSteps = [
  { title: 'Process Audit', body: "We spend time understanding how your team actually works — what takes too long, what gets dropped, what could be systematized. We also bring what we've already built: if an existing NTC tool fits your workflow, you benefit from it immediately." },
  { title: 'Automation Roadmap', body: "We identify the highest-impact automations for your business and prioritize them by time saved vs. implementation effort. You'll see the ROI estimate before we build anything." },
  { title: 'Build & Integrate', body: "We build the automations into your existing tools — no ripping and replacing your current stack. Security guardrails are configured before anything goes live." },
  { title: 'Measure ROI', body: "You'll see exactly how many hours were recovered and what that means in dollars. We track it, report it, and keep optimizing." },
]

const securityChecklist = [
  'Role-based access controls for all AI tools',
  'Document management permissions reviewed and locked',
  'Microsoft Copilot scoped to appropriate data boundaries',
  'Staff training on what AI can and can\'t access',
  'Ongoing monitoring as AI tools and permissions evolve',
]

export default function AIAutomation() {
  return (
    <>
      <PageMeta
        title={pageMeta.aiAutomation.title}
        description={pageMeta.aiAutomation.description}
        canonical={pageMeta.aiAutomation.canonical}
        schema={aiAutomationSchema}
      />
      <main className="min-h-screen pt-24 pb-16 px-6">
        {/* Hero */}
        <section className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase text-white mb-6">
            Your team shouldn't spend hours on work that AI can do in minutes.
          </h1>
          <p className="font-body text-cream-dim text-lg mb-8">
            We use AI inside our own operation every day — to deliver compliance documents faster, manage client environments more efficiently, and solve problems before they surface. Then we bring those same capabilities to your business. With the security guardrails built in from day one.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/contact" variant="primary">
              Book a Free Automation Audit
            </Button>
            <a
              href="#proof"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-body font-medium border-2 border-accent text-accent hover:bg-accent/10 transition-colors"
            >
              See What We've Built
            </a>
          </div>
          <p className="font-body text-cream-dim text-sm mt-6">
            Comparing Claude, ChatGPT, Gemini, Copilot, Meta AI, and Grok?{' '}
            <Link to="/services/ai-automation/compare" className="text-accent hover:underline">
              See the comparison matrix →
            </Link>
          </p>
        </section>

        {/* Proof Section */}
        <section id="proof" className="max-w-6xl mx-auto mb-24">
          <SectionLabel className="mb-4 text-center">What This Looks Like in Practice</SectionLabel>
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-lg border-l-4"
              style={{ backgroundColor: 'var(--bg-3)', borderColor: 'var(--accent)' }}
            >
              <h2 className="font-display font-bold text-2xl uppercase text-white mb-4">
                Compliance & Security Documentation
              </h2>
              <p className="font-body text-cream-dim mb-6">
                Compliance documentation is one of the most time-consuming tasks in any regulated industry — and one of the areas where AI delivers the most immediate, measurable return.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="font-display font-black text-2xl mb-1" style={{ color: 'var(--accent)' }}>20 hrs → 3 hrs</div>
                  <div className="font-body text-cream-dim text-sm">Security risk assessments</div>
                </div>
                <div>
                  <div className="font-display font-black text-2xl mb-1" style={{ color: 'var(--accent)' }}>4 hrs → 30 min</div>
                  <div className="font-body text-cream-dim text-sm">Security questionnaires</div>
                </div>
              </div>
              <p className="font-body text-cream-dim mb-4">
                A client we onboarded 30 days ago needed a detailed security measures assessment to close a new contract. We delivered it in 30 minutes — accurate, thorough, and ready to send. That's not just time saved. That's revenue enabled.
              </p>
              <p className="font-body text-cream-dim mb-0">
                We handle HIPAA documentation, PCI compliance, internal security policies, and vendor security assessments. If your business operates in a regulated industry, this alone pays for a significant portion of your NTC engagement.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-lg"
              style={{ backgroundColor: 'var(--bg-3)' }}
            >
              <h2 className="font-display font-bold text-2xl uppercase text-white mb-4">
                Service Desk & Internal Operations
              </h2>
              <p className="font-body text-cream-dim">
                We've been running AI-assisted ticket management inside our own service desk for five months. Faster diagnosis. Faster documentation. Faster resolution across ticket routing, issue identification, and response drafting.
              </p>
              <p className="font-body text-cream-dim mt-4">
                You probably won't notice the difference directly. You'll just notice that problems get solved faster. Which is exactly the point.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-lg"
              style={{ backgroundColor: 'var(--bg-3)' }}
            >
              <h2 className="font-display font-bold text-2xl uppercase text-white mb-4">
                Proprietary Google Workspace Management Platform
              </h2>
              <p className="font-body text-cream-dim mb-4">
                <span className="font-display font-black text-2xl" style={{ color: 'var(--accent)' }}>5 hrs saved/client/month</span>
                {' '}— We built our own internal platform to manage Google Workspace environments across our entire client base — delegated mailbox access, security dashboards, backend administration that used to require hours of manual work per client.
              </p>
              <p className="font-body text-cream-dim">
                We built it because no off-the-shelf tool did it well enough. We're now preparing to offer this platform to other MSPs. That's the level we operate at — we don't just use tools, we build them.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-lg"
              style={{ backgroundColor: 'var(--bg-3)' }}
            >
              <h2 className="font-display font-bold text-2xl uppercase text-white mb-4">
                Custom Application Development
              </h2>
              <p className="font-body text-cream-dim">
                For clients who need more than managed IT, we build. A client in the security industry needed to replace spreadsheets with a secure, auditable web application — with proper data archiving instead of deletion and access controls that spreadsheets simply can't provide. We built it.
              </p>
              <p className="font-body text-cream-dim mt-4">
                We've also built HIPAA-compliant operational software for healthcare clients. If your business has a manual process that a custom application could solve, we can scope, build, and maintain it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* AI Security Callout */}
        <section
          className="py-16 px-8 mb-24 rounded-xl"
          style={{ backgroundColor: 'var(--bg-3)', border: '2px solid var(--accent)' }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display font-black text-3xl md:text-4xl uppercase text-white mb-6">
              AI that can access everything is AI that can share the wrong thing.
            </h2>
            <p className="font-body text-cream-dim mb-8 leading-relaxed">
              Before we rolled out any AI service — internally or for clients — we built the security layer first. That means role-based access controls so AI tools only touch what they're supposed to. It means Microsoft Copilot and document management configured so sensitive information stays where it belongs. It means your staff can use AI tools confidently, without the risk of inadvertently exposing client data, trade secrets, or protected health information.
            </p>
            <p className="font-body font-semibold text-white mb-6">Most AI implementations skip this step. We start here.</p>
            <ul className="text-left space-y-2 max-w-md mx-auto">
              {securityChecklist.map((item) => (
                <li key={item} className="flex items-start gap-3 font-body text-cream-dim">
                  <Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Example Automations */}
        <section className="max-w-6xl mx-auto mb-24">
          <SectionLabel className="mb-4 text-center">What We Automate</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {exampleAutomations.map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-lg flex items-start gap-4"
                style={{ backgroundColor: 'var(--bg-3)' }}
              >
                <a.icon className="w-8 h-8 shrink-0" style={{ color: 'var(--accent)' }} />
                <p className="font-body text-cream-dim">{a.label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="max-w-4xl mx-auto mb-24">
          <SectionLabel className="mb-4 text-center">Our Process</SectionLabel>
          <h2 className="font-display font-extrabold text-3xl uppercase text-white text-center mb-12">
            How we find your automation wins.
          </h2>
          <div className="space-y-8">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-6"
              >
                <span aria-hidden="true" className="font-display font-black text-2xl shrink-0" style={{ color: 'var(--accent)', opacity: 0.85 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-display font-bold text-xl uppercase text-white mb-2">{step.title}</h3>
                  <p className="font-body text-cream-dim">{step.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-black text-3xl md:text-4xl uppercase text-white mb-4">
            Find out what you're leaving on the table.
          </h2>
          <p className="font-body text-cream-dim mb-8">
            We'll map your highest-impact automation opportunities in a free 30-minute audit — and give you a realistic estimate of hours and dollars recovered per week.
          </p>
          <Button to="/contact" variant="primary">
            Book Your Free Automation Audit
          </Button>
        </section>
      </main>
    </>
  )
}
